import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IEditAccountForm } from '../../admin/account/edit/edit-form.model';
import { IHubCreateForm } from '../../admin/account/hub/create-hub/create-hub-form.model';
import { IAddUserForm } from '../../admin/account/users/add-user-form.model';
import { Account, AddUserResponse, AdyenPaymentsResponse, CheckPublishedContentResponse, CreateAccountResponse, Hub, HubSubscriptionsResponse, HubSubscriptionView, ICreateAccountForm, NetsPaymentResponse, PaymentType, PaySubscriptionResponse, RetryPaymentResponse, UserRole } from '../models';
import { AccountPaymentMethodRequest } from '../models/requests/accountPaymentMethodRequest.model';
import { HubUpgradePaymentMethodRequest } from '../models/requests/hubUpgradePaymentMethodRequest.model';
import { NetsPaymentRequest } from '../models/requests/netsPaymentRequest.model';
import { ISubscriptionPaymentRequest } from '../models/requests/subscriptionPaymentRequest.model';
import { TranslatePaymentMethodRequest } from '../models/requests/translatePaymentMethodRequest.model';
import { AccountBillingResponse } from '../models/responses/accountBillingResponse.model';
import { AccountProductsResponse } from '../models/responses/accountProductsResponse.model';
import { CreateHubResponse } from '../models/responses/createHubResponse.model';
import { PaymentMethodResponse } from '../models/responses/paymentMethodResponse.model';
import { TranslationPaymentResponse } from '../models/responses/translationPaymentResponse.model';
import { UpgradeHubResponse } from '../models/responses/upgradeHubResponse.model';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  constructor(private http: HttpClient) {
  }

  getAccounts() : Observable<Account[]> {
    return this.http.get<Account[]>('/accounts/me');
  }

  createAccount(form: ICreateAccountForm, force = false) : Observable<CreateAccountResponse> {
    var params = new HttpParams().set("force", force ? "true" : "false");
    return this.http.post<CreateAccountResponse>('/accounts', form, { params });
  }

  edit(id: number) {
    return this.http.get<Account>('/accounts/' + id + '/edit');
  }

  saveAccount(id: number, form: IEditAccountForm) : Observable<void> {
    return this.http.post<void>('/accounts/' + id + '/edit', form);
  }
  
  getHubs(accountId: number) : Observable<Hub[]> {
    return this.http.get<Hub[]>('/admin/hubs/me/').pipe(
      map(hubs => hubs.filter(h => h.accountId === accountId))
    );
  }

  getUsers(id: number): Observable<UserRole[]> {
    return this.http.get<UserRole[]>('/accounts/' + id + '/users');
  }

  removeUser(id: number, userId: number) : Observable<void> {
    return this.http.delete<void>('/accounts/' + id + '/users/' + userId, {});
  }

  addUser(id: number, form: IAddUserForm) : Observable<AddUserResponse> {
    form.roleId = parseInt(form.roleId.toString());
    return this.http.post<AddUserResponse>('/accounts/' + id + '/users', form);
  }

  saveUser(id: number, userId: number, roleId: number): Observable<void> {
    return this.http.post<void>('/accounts/' + id + '/users/' + userId, { roleId: roleId });
  }  

  addSupportUser(id: number) : Observable<void> {
    return this.http.post<void>('/accounts/' + id + '/users/support', {});
  }  

  getBilling(id: number) {
    return this.http.get<AccountBillingResponse>('/accounts/' + id + '/billing');
  }

  createPaymentMethod(id: number, returnUrl: string): Observable<PaymentMethodResponse> {
    var params = new HttpParams()
        .set('returnUrl', returnUrl);
    return this.http.get<PaymentMethodResponse>('/accounts/' + id + '/paymentmethod', { params: params });
  }

  setPaymentMethod(id: number, request: NetsPaymentRequest) : Observable<void> {
    return this.http.post<void>('/accounts/' + id + '/paymentmethod', request);
  }

  savePaymentMethod(id: number, request: AccountPaymentMethodRequest) : Observable<void> {
    return this.http.post<void>('/accounts/' + id + '/paymentmethod/success', request);
  }

  getProducts(id: number) : Observable<AccountProductsResponse> {
    return this.http.get<AccountProductsResponse>('/accounts/' + id + '/products');
  }

  getHubSubscriptions(hubId: number) : Observable<HubSubscriptionsResponse> {
    return this.http.get<HubSubscriptionsResponse>('/admin/hubs/' + hubId + '/subscriptions');
  }

  getHubUpgrade(id: number, subscriptionId: number, subscriptionLevelId: number, returnUrl: string, netsPaymentId?: string): Observable<UpgradeHubResponse> {
    var params = new HttpParams()
      .set('returnUrl', returnUrl);
    if (!!netsPaymentId) {
      params = params.set('netsPaymentId', netsPaymentId);
    }
    return this.http.get<UpgradeHubResponse>('/accounts/' + id + '/subscription/' + subscriptionId + '/upgrade/' + subscriptionLevelId, { params: params });
  }

  payHubUpgrade(request: HubUpgradePaymentMethodRequest): Observable<AdyenPaymentsResponse> {
    return this.http.post<AdyenPaymentsResponse>('/accounts/' + request.accountId + '/subscription/' + request.subscriptionId + '/upgrade/' + request.subscriptionLevelId, 
    {
      netsPaymentId: request.netsPaymentId,
      paymentType: request.paymentType
    });
  }

  payHubUpgradeSuccess(id: number, subscriptionId: number, subscriptionLevelId: number, paymentId: number): Observable<void> {
    return this.http.put<void>('/accounts/' + id + '/subscription/' + subscriptionId + '/upgrade/' + subscriptionLevelId, {
      paymentId
    });
  }

  translation(accountId: number, hubId: number, languages: string, returnUrl: string, netsPaymentId: string): Observable<TranslationPaymentResponse> {
    var params = new HttpParams()
      .set('languages', languages)
      .set('returnUrl', returnUrl);
    if (!!netsPaymentId) {
      params = params.set('netsPaymentId', netsPaymentId);
    }
    return this.http.get<TranslationPaymentResponse>('/accounts/' + accountId + '/translation/payment/' + hubId, { params: params });
  }

  payTranslation(request: TranslatePaymentMethodRequest): Observable<AdyenPaymentsResponse> {
    return this.http.post<AdyenPaymentsResponse>('/accounts/' + request.accountId + '/translation/payment/' + request.hubId, {
      languages: request.languages,
      netsPaymentId: request.netsPaymentId,
      businessId: request.businessId
    });
  }

  hubDowngrade(id: number, subscriptionId: number, subscriptionLevelId: number): Observable<void> {
    return this.http.post<void>('/accounts/' + id + '/subscription/' + subscriptionId + '/downgrade/' + subscriptionLevelId, {});
  }  
  
  getSubscription(languageId: number, accountId: number, subscriptionLevelId: number, period: number, checkoutUrl: string, trial: boolean, hubVerificationId?: number, hubId?: number, netsPaymentId?: string): Observable<PaySubscriptionResponse> {
    var params = new HttpParams()
        .set('languageId', languageId.toString())
        .set('subscriptionLevelId', subscriptionLevelId.toString())
        .set('period', period.toString())
        .set('trial', trial.toString())
        .set('checkoutUrl', checkoutUrl);
    if (!!netsPaymentId) {
      params = params.set('netsPaymentId', netsPaymentId);
    }
    if (!!hubVerificationId) {
        params = params.set('hubVerificationId', hubVerificationId.toString());
    }
    if (!!hubId) {
      params = params.set('hubId', hubId.toString());
    }
    
    return this.http.get<PaySubscriptionResponse>('/accounts/' + accountId + '/hub/pay', { params: params });
  }

  createHub(accountId: number, form: IHubCreateForm, force = false) : Observable<CreateHubResponse> {
    var params = new HttpParams().set('force', force ? 'true' : 'false');
    return this.http.post<CreateHubResponse>('/accounts/' + accountId + '/hub', form, { params });
  }

  payHub(accountId: number, data: ISubscriptionPaymentRequest): Observable<NetsPaymentResponse> {
    return this.http.post<any>('/accounts/' + accountId + '/hub/pay', data);
  }

  updateHubPayment(languageId: number, netsPaymentId: string, accountId: number, subscriptionLevelId: number, period: number, hubVerificationId?: number, hubId?: number) : Observable<void> {
    var data = {
      languageId,
      subscriptionLevelId,
      period,
      hubVerificationId,
      hubId
    };
    return this.http.put<void>('/accounts/' + accountId + '/hub/pay/' + netsPaymentId, data);
  }

  payHubSuccess(accountId: number, hubId: number, subscriptionLevelId: number, paymentId: number, paymentPeriod: number, hubVerificationId: number): Observable<void> {
    var data = {
      hubId,
      subscriptionLevelId,
      paymentId,
      paymentPeriod,
      hubVerificationId
    };
    return this.http.put<void>('/accounts/' + accountId + '/hub/pay', data);
  }  

  confirmAccount(accountId: number) : Observable<void> {
    return this.http.post<void>('/accounts/' + accountId + '/confirm', {});
  }

  confirmHub(accountId: number, hubId: number) : Observable<void> {
    return this.http.post<void>('/accounts/' + accountId + '/confirm/hub/' + hubId, {});
  }  

  getPayment(accountId: number, paymentId: number, returnUrl: string, netsPaymentId?: string): Observable<RetryPaymentResponse> {
    var params = new HttpParams()
      .set("returnUrl", returnUrl);
    if (!!netsPaymentId) {
      params = params.set('netsPaymentId', netsPaymentId);
    }
    return this.http.get<RetryPaymentResponse>('/accounts/' + accountId + '/payment/' + paymentId, { params: params });
  }

  retryPayment(accountId: number, paymentId: number, data: NetsPaymentRequest) : Observable<AdyenPaymentsResponse> {
    return this.http.post<AdyenPaymentsResponse>('/accounts/' + accountId + '/payment/' + paymentId, data);
  }

  retryPaymentSuccess(accountId: number, paymentId: number, paymentType: PaymentType) : Observable<void> {
    var data = { paymentType };
    return this.http.post<void>('/accounts/' + accountId + '/payment/' + paymentId + '/success', data);
  } 

  hubHasPublishedContent(hubId: number): Observable<CheckPublishedContentResponse> {
    return this.http.get<CheckPublishedContentResponse>('/admin/hubs/' + hubId + '/haspublishedcontent');
  }

  setSubscriptionPaymentPeriod(accountId: number, subscriptionId: number, period: number): Observable<void> {
    return this.http.post<void>(`/accounts/${accountId}/subscription/${subscriptionId}/paymentperiod`, { period });
  }
}